import { Injectable } from '@angular/core';
import { environment } from '../environments/environment';

interface IrrigatorExtentResponse {
  projection: string;
  extent: [number, number, number, number];
}

export type IrrigatorExtentResult =
  | { kind: 'ready'; extent: [number, number, number, number] }
  | { kind: 'empty' }
  | { kind: 'unauthorized' }
  | { kind: 'unavailable' };

@Injectable({ providedIn: 'root' })
export class IrrigatorExtentService {
  async getBufferedCurrentExtent(headers: Record<string, string>): Promise<IrrigatorExtentResult> {
    try {
      const response = await fetch(`${environment.apiWmsUrl}/irrigators/extent`, { headers });

      if (response.status === 204) {
        return { kind: 'empty' };
      }

      if (response.status === 401 || response.status === 403) {
        return { kind: 'unauthorized' };
      }

      if (!response.ok) {
        return { kind: 'unavailable' };
      }

      const result = await response.json() as IrrigatorExtentResponse;
      if (result.projection !== 'EPSG:3857' || !this.isValidExtent(result.extent)) {
        return { kind: 'unavailable' };
      }

      return {
        kind: 'ready',
        extent: this.getBufferedExtent(result.extent)
      };
    } catch {
      return { kind: 'unavailable' };
    }
  }

  private isValidExtent(extent: number[]): extent is [number, number, number, number] {
    return extent.length === 4 &&
      extent.every((value) => Number.isFinite(value)) &&
      extent[0] <= extent[2] &&
      extent[1] <= extent[3];
  }

  private getBufferedExtent(extent: [number, number, number, number]): [number, number, number, number] {
    const width = extent[2] - extent[0];
    const height = extent[3] - extent[1];
    const buffer = Math.max(width, height, 120) * 0.2;

    return [
      extent[0] - buffer,
      extent[1] - buffer,
      extent[2] + buffer,
      extent[3] + buffer
    ];
  }
}
